/**
 * Adoption report types and utilities
 */

import { Clinician, ClinicianData, toClinicianData } from './clinician.types';
import {
  VisitCompletionData,
  VisitCompletionRawData,
  toVisitCompletionData,
} from './visitCompletion.types';

/**
 * Raw adoption report data from API
 */
export interface AdoptionReportRawData {
  clinician_username: string;
  oasis?: VisitCompletionRawData;
  regular?: VisitCompletionRawData;
  clinician: ClinicianData;
}

/**
 * Processed adoption report entry with combined data
 */
export interface AdoptionReportEntry {
  clinicianUsername: string;
  oasis: VisitCompletionData;
  regular: VisitCompletionData;
  clinician: Clinician;
}

/**
 * Raw branch report response from API
 */
export interface BranchReportResponse {
  clinician_meta: Record<string, ClinicianData>;
  reports: Array<{
    branch_id: string;
    branch_name: string;
    visit_breakdowns: Record<
      string,
      {
        // Oasis fields
        internally_completed_oasis?: number;
        externally_completed_oasis?: number;
        canceled_oasis?: number;
        filling_failed_oasis?: number;
        total_oasis?: number;

        // Regular fields
        internally_completed_regular?: number;
        externally_completed_regular?: number;
        canceled_regular?: number;
        filling_failed_regular?: number;
        total_regular?: number;
      }
    >;
  }>;
}

/**
 * Processed branch report entry
 */
export interface BranchReportEntry {
  branchName: string;
  branchId: string;
  clinician: Clinician;
  oasis: VisitCompletionData;
  regular: VisitCompletionData;
}

/**
 * Converts raw adoption report data from API to processed format
 */
export const toAdoptionReportEntry = (
  data: AdoptionReportRawData
): AdoptionReportEntry => ({
  clinicianUsername: data.clinician_username,
  oasis: toVisitCompletionData(data.oasis),
  regular: toVisitCompletionData(data.regular),
  clinician: toClinicianData(data.clinician),
});

/**
 * Helper to process branch report data
 */
export const processBranchReportData = (
  data: BranchReportResponse
): BranchReportEntry[] => {
  const flattenedData: BranchReportEntry[] = [];

  data.reports.forEach(branchReport => {
    Object.entries(branchReport.visit_breakdowns).forEach(
      ([username, breakdown]) => {
        const clinicianMeta = data.clinician_meta[username];
        if (clinicianMeta) {
          // Map flattened branch report data to VisitCompletionRawData structure
          // Note: Mapping 'total' to 'not_started' because the helper functions
          // treat 'not_started' as the Total count to calculate pending.

          const oasisData: VisitCompletionRawData = {
            emr_filled: breakdown.internally_completed_oasis,
            external_completion: breakdown.externally_completed_oasis,
            canceled_in_app: breakdown.canceled_oasis,
            not_started: breakdown.total_oasis,
          };

          const regularData: VisitCompletionRawData = {
            emr_filled: breakdown.internally_completed_regular,
            external_completion: breakdown.externally_completed_regular,
            canceled_in_app: breakdown.canceled_regular,
            not_started: breakdown.total_regular,
          };

          flattenedData.push({
            branchName: branchReport.branch_name,
            branchId: branchReport.branch_id,
            clinician: toClinicianData(clinicianMeta),
            oasis: toVisitCompletionData(oasisData),
            regular: toVisitCompletionData(regularData),
          });
        }
      }
    );
  });

  return flattenedData;
};

/**
 * Combined totals utilities for adoption report entries
 */

export const getTotalNotStarted = (
  entry: AdoptionReportEntry | BranchReportEntry
): number => {
  return entry.oasis.notStarted + entry.regular.notStarted;
};

export const getTotalEmrFilled = (
  entry: AdoptionReportEntry | BranchReportEntry
): number => {
  return entry.oasis.emrFilled + entry.regular.emrFilled;
};

export const getTotalExternalCompletion = (
  entry: AdoptionReportEntry | BranchReportEntry
): number => {
  return entry.oasis.externalCompletion + entry.regular.externalCompletion;
};

export const getTotalCanceledInApp = (
  entry: AdoptionReportEntry | BranchReportEntry
): number => {
  return entry.oasis.canceledInApp + entry.regular.canceledInApp;
};

export const getTotalVisits = (
  entry: AdoptionReportEntry | BranchReportEntry
): number => {
  return (
    getTotalNotStarted(entry) +
    getTotalEmrFilled(entry) +
    getTotalExternalCompletion(entry) +
    getTotalCanceledInApp(entry)
  );
};

export const getPendingVisits = (
  entry: AdoptionReportEntry | BranchReportEntry
): number => {
  return Math.max(
    0,
    getTotalNotStarted(entry) -
      (getTotalEmrFilled(entry) +
        getTotalExternalCompletion(entry) +
        getTotalCanceledInApp(entry))
  );
};

export const getRogerPercentage = (
  entry: AdoptionReportEntry | BranchReportEntry
): string => {
  const total = getTotalVisits(entry);
  return total > 0
    ? ((getTotalEmrFilled(entry) / total) * 100).toFixed(1)
    : '0';
};

export const getExternalPercentage = (
  entry: AdoptionReportEntry | BranchReportEntry
): string => {
  const total = getTotalVisits(entry);
  return total > 0
    ? ((getTotalExternalCompletion(entry) / total) * 100).toFixed(1)
    : '0';
};

export const getPendingPercentage = (
  entry: AdoptionReportEntry | BranchReportEntry
): string => {
  const total = getTotalVisits(entry);
  return total > 0 ? ((getPendingVisits(entry) / total) * 100).toFixed(1) : '0';
};

export const getRogerVsExternalPercentage = (
  entry: AdoptionReportEntry | BranchReportEntry
): string => {
  const totalCompleted =
    getTotalEmrFilled(entry) + getTotalExternalCompletion(entry);
  return totalCompleted > 0
    ? ((getTotalEmrFilled(entry) / totalCompleted) * 100).toFixed(1)
    : '0';
};
